import BaseModel from 'volta/models/base-model';

import { attr, belongsTo, hasMany } from '@ember-data/model';
import { isEmpty } from '@ember/utils';

import type { IResourceName } from 'volta/models/base-model';
import type BomNode from './bom-node';
import type SkuModel from './sku';
import type RouteOperation from './route-operation';
import { Type } from '@warp-drive/core-types/symbols';

export const ResourceName: IResourceName = {
  singular: 'bom',
  plural: 'boms'
};

export default class Bom extends BaseModel {
  @attr('number') partsCount!: number;
  @attr('number') maxLevel!: number;
  @attr('number') clt!: number;
  @belongsTo('sku', { async: false, inverse: null }) sku!: SkuModel;

  // not async because of https://github.com/emberjs/data/issues/5023 – also see models/bom-node.js
  @hasMany('bom-node', { async: false, inverse: 'bom' }) nodes!: BomNode[];

  @hasMany('route-operation', { async: false, inverse: null }) operations!: RouteOperation[];

  get skuId() {
    return this.sku.id;
  }
  get partCode() {
    return this.sku.part.code;
  }
  get skuDescription() {
    return this.sku.skuDescription;
  }
  get warehouseCode() {
    return this.sku.warehouse.code;
  }
  get warehouseDesc() {
    return this.sku.warehouse.description;
  }

  get partCell() {
    return {
      code: this.partCode,
      description: this.skuDescription,
      link: 'protected.boms.sku',
      linkParam: this.skuId
    };
  }

  get warehouseCell() {
    return {
      code: this.sku.warehouse.code,
      description: this.sku.warehouse.description
    };
  }

  get childNodes() {
    return this.nodes.filter((node) => Boolean(node.parentNode?.id));
  }

  get hasChildren() {
    return !isEmpty(this.childNodes);
  }

  get actualMaxLevel() {
    return this.nodes.reduce((acc, node) => {
      if (node.level > acc) {
        acc = node.level;
      }
      return acc;
    }, 1);
  }

  get hasOperations() {
    return this.operations.length > 0;
  }

  [Type] = 'bom' as const;
}
